/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { OverflowTagList } from "../overflow_tag_list";
import {
  TagRoot,
  TagLabel,
  TagActionsWrapper,
  TagIconButton,
} from "./styles";

const TEST_ID_PREFIX = "subgroups";

const RestaurantSubGroupTag = React.memo(
  ({ item, canEdit, canDelete, onEdit, onDelete, isMeasurement = false }) => {
    return (
      <TagRoot
        aria-hidden={isMeasurement ? "true" : undefined}
        data-testid={isMeasurement ? undefined : `subgroup-tag-${item.id}`}
      >
        <TagLabel
          component="span"
          data-testid={isMeasurement ? undefined : `subgroup-tag-label-${item.id}`}
        >
          {item.name}
        </TagLabel>

        <TagActionsWrapper>
          {canEdit && (
            <TagIconButton
              size="small"
              onClick={() => onEdit?.(item)}
              data-testid={isMeasurement ? undefined : `subgroup-tag-edit-${item.id}`}
              aria-label={isMeasurement ? undefined : `Edit ${item.name}`}
              tabIndex={isMeasurement ? -1 : undefined}
            >
              <EditOutlinedIcon fontSize="inherit" />
            </TagIconButton>
          )}

          {canDelete && (
            <TagIconButton
              size="small"
              onClick={() => onDelete?.(item)}
              data-testid={isMeasurement ? undefined : `subgroup-tag-delete-${item.id}`}
              aria-label={isMeasurement ? undefined : `Delete ${item.name}`}
              tabIndex={isMeasurement ? -1 : undefined}
            >
              <CloseIcon fontSize="inherit" />
            </TagIconButton>
          )}
        </TagActionsWrapper>
      </TagRoot>
    );
  },
);

RestaurantSubGroupTag.displayName = "RestaurantSubGroupTag";

export const RenderRestaurantSubGroups = React.memo(
  ({
    data = [],
    canEdit = false,
    canDelete = false,
    onEdit,
    onDelete,
    emptyLabel = "No Restaurant Sub Groups",
  }) => {
    const renderTag = React.useCallback(
      (item, { isMeasurement }) => (
        <RestaurantSubGroupTag
          item={item}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={onEdit}
          onDelete={onDelete}
          isMeasurement={isMeasurement}
        />
      ),
      [canEdit, canDelete, onEdit, onDelete],
    );

    return (
      <OverflowTagList
        data={data}
        renderTag={renderTag}
        emptyLabel={emptyLabel}
        testIdPrefix={TEST_ID_PREFIX}
        overflowAriaLabel={(count) => `${count} more subgroups`}
      />
    );
  },
);

RenderRestaurantSubGroups.displayName = "RenderRestaurantSubGroups";
